import type { MessageType } from "common/types/userNotification";
import type { ReactNode } from "react";
import { useState } from "react";
import cx from "ui/cx";
import IconClose from "ui/icons/IconClose";
import IconError from "ui/icons/status/IconError";
import IconInfo from "ui/icons/status/IconInfo";
import IconSuccess from "ui/icons/status/IconSuccess";
import IconWarning from "ui/icons/status/IconWarning";
import IconWarningInverted from "ui/icons/status/IconWarningInverted";
import styles from "./Message.module.scss";

type MessageVariant = "standard" | "inverted" | "banner";

interface MessageProps {
  children: ReactNode;
  variant?: MessageVariant;
  messageType?: MessageType;
  dismissable?: boolean;
  sticky?: boolean;
  centered?: boolean;
  className?: string;
  marginBottom?: boolean;
}

export function Message({
  children,
  messageType = "default",
  variant = "inverted",
  dismissable = false,
  sticky = false,
  centered = false,
  className,
  marginBottom = false,
}: MessageProps) {
  const classNames = cx(
    {
      styles,
      wrapper: true,
      [messageType]: true,
      [`variant-${variant}`]: true,
      sticky,
      centered,
    },
    marginBottom && styles.marginBottom,
    className,
  );
  const [visible, setVisible] = useState(true);

  let icon: ReactNode = null;
  const size = 18;

  switch (messageType) {
    case "default": {
      icon = null;
      break;
    }
    case "success": {
      icon = <IconSuccess size={size} />;
      break;
    }
    case "info": {
      icon = <IconInfo size={size} />;
      break;
    }
    case "warning": {
      if (variant === "inverted") {
        icon = <IconWarningInverted size={size} />;
      } else {
        icon = <IconWarning size={size} />;
      }
      break;
    }
    case "error": {
      icon = <IconError size={size} />;
      break;
    }
  }

  if (!visible) {
    return null;
  }

  return (
    <div className={classNames}>
      <div className="interior">
        {icon && <div className="icon-container">{icon}</div>}
        <div className="notification-text">{children}</div>
        {dismissable && (
          <div className="close-container" onClick={() => setVisible(false)}>
            <IconClose size={18} />
          </div>
        )}
      </div>
    </div>
  );
}
